import { FC, Fragment } from "react";
import { Controller } from "react-hook-form";

import { FormError } from "../FormError";
import "./checkbox.css";

interface IProps {
  title: string;
  subTitle?: string;
  name: string;
  id?: string;
  readOnly?: boolean;
  disabled?: boolean;
  onChange?: (val: boolean) => void;
  control: any;
}

interface CustomCheckboxProps {
  title: string;
  subTitle?: string;
  name: string;
  id?: string;
  readOnly?: boolean;
  disabled?: boolean;
  onChange?: (val: boolean | string) => void;
  control: any;
  valueType: "boolean" | "string";
  classes?: string;
  hideErrorMsg?: boolean;
}

interface CustomCheckboxProps {
  title: string;
  subTitle?: string;
  name: string;
  id?: string;
  readOnly?: boolean;
  disabled?: boolean;
  onChange?: (val: boolean | string) => void;
  control: any;
  valueType: "boolean" | "string";
  classes?: string;
  hideErrorMsg?: boolean;
}

interface CustomCheckboxWithOptionsProps {
  title: string;
  subTitle?: string;
  name: string;
  id: string;
  readOnly?: boolean;
  disabled?: boolean;
  onChange?: (val: string) => void;
  control: any;
  options: { label: string; value: string }[];
  classes?: string;
}

export const Checkbox: FC<IProps> = ({
  title,
  subTitle,
  name,
  id = name,
  readOnly = false,
  disabled = false,
  onChange: _onChange,
  control
}) => {
  return (
    <div className="mb-4">
      <p className="text-base font-semibold mb-2">{title}</p>
      {subTitle ? <p className="text-sm font-light italic text-gray-500 mb-2">{subTitle}</p> : null}

      <Controller
        name={name}
        control={control}
        render={({ field: { name, onBlur, onChange, value }, fieldState: { error } }) => (
          <article>
            <section className="flex items-center space-x-3">
              <div className="checkbox-wrapper-4">
                <input
                  type="checkbox"
                  id={id}
                  name={name}
                  onChange={e => {
                    const val = e.target.checked;
                    onChange(val);

                    if (typeof _onChange === "function") _onChange(val);
                  }}
                  checked={typeof value === "string" ? value === "yes" : value}
                  onBlur={onBlur}
                  disabled={disabled}
                  readOnly={readOnly}
                />
              </div>
            </section>
            {error?.message ? <FormError error={error.message} id={id} /> : null}
          </article>
        )}
      />
    </div>
  );
};

export const CustomCheckbox: FC<CustomCheckboxProps> = ({
  control,
  title,
  name,
  id = name,
  readOnly,
  disabled,
  valueType = "boolean",
  classes = "mb-4",
  onChange: _onChange,
  hideErrorMsg = false
}) => {
  return (
    <div className={classes}>
      <Controller
        name={name}
        control={control}
        render={({ field: { name, onBlur, onChange, value }, fieldState: { error } }) => (
          <article>
            <section className="flex items-center space-x-3">
              <div className="checkbox-wrapper-4">
                <input
                  type="checkbox"
                  id={name}
                  name={name}
                  onChange={e => {
                    const val = e.target.checked;
                    if (valueType === "boolean") {
                      onChange(val);

                      if (typeof _onChange === "function") _onChange(val);
                    } else {
                      onChange(val ? "yes" : "no");

                      if (typeof _onChange === "function") _onChange(val ? "yes" : "no");
                    }
                  }}
                  checked={typeof value === "string" ? value === "yes" : value}
                  onBlur={onBlur}
                  disabled={disabled}
                  readOnly={readOnly}
                  className="inp-cbx w-4 h-4"
                />
                <label className="cbx" htmlFor={name}>
                  <span>
                    <svg width="12px" height="10px">
                      <use xlinkHref="#check-4"></use>
                    </svg>
                  </span>
                  <span className="italic w-[calc(100%_-_18px)]">{title}</span>
                </label>
                <svg className="inline-svg">
                  <symbol id="check-4" viewBox="0 0 12 10">
                    <polyline points="1.5 6 4.5 9 10.5 1"></polyline>
                  </symbol>
                </svg>
              </div>
            </section>
            {error && !hideErrorMsg ? <FormError error={error.message} id={id} /> : null}
          </article>
        )}
      />
    </div>
  );
};

/**
 * @description checkbox with custom value
 * @param {*} control
 * @param {*} name
 * @param {*} options
 * @returns
 */
export const CustomCheckboxWithOptions: FC<CustomCheckboxWithOptionsProps> = ({
  control,
  title,
  name,
  id = name,
  readOnly,
  disabled,
  classes = "mb-4",
  options,
  subTitle,
  onChange: _onChange
}) => {
  return (
    <div className={classes}>
      <p className="text-base font-semibold mb-2">{title}</p>
      {subTitle ? <p className="text-sm font-light italic text-gray-500 mb-2">{subTitle}</p> : null}
      <Controller
        name={name}
        control={control}
        render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error } }) => (
          <article className="space-y-2">
            {options.map((option, idx) => (
              <Fragment key={idx}>
                <section className="flex items-center space-x-3">
                  <div className="checkbox-wrapper-4">
                    <input
                      type="checkbox"
                      ref={ref}
                      id={name + option.value}
                      name={name}
                      onChange={e => {
                        if (e.target.checked) {
                          onChange(option.value);
                          if (typeof _onChange === "function") _onChange(option.value);
                        } else onChange("");
                      }}
                      checked={value === option?.value}
                      onBlur={onBlur}
                      disabled={disabled}
                      readOnly={readOnly}
                      className="inp-cbx w-4 h-4"
                    />
                    <label className="cbx" htmlFor={name + option.value}>
                      <span>
                        <svg width="12px" height="10px">
                          <use xlinkHref="#check-4"></use>
                        </svg>
                      </span>
                      <span className="italic">{option.label}</span>
                    </label>
                    <svg className="inline-svg">
                      <symbol id="check-4" viewBox="0 0 12 10">
                        <polyline points="1.5 6 4.5 9 10.5 1"></polyline>
                      </symbol>
                    </svg>
                  </div>
                </section>
              </Fragment>
            ))}
            {error ? <FormError error={error.message} id={id} /> : null}
          </article>
        )}
      />
    </div>
  );
};
